/*
 * PartnersPage — /partners
 * =========================
 * Iter 69 · matches AcademyLandingPage / TeamPage design tokens
 * (bg-black, Bebas Neue, left-aligned hero, badge, yellow accent underline).
 * Iter 70 · Supports ?scope=first_team|academy to segregate partners by team.
 */
import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Handshake, ChevronRight, Image as ImageIcon } from "lucide-react";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  technical: "Τεχνικος Συνεργατης",
  coaching: "Coaching",
  sponsor: "Χορηγος",
  academic: "Ακαδημαϊκος",
  medical: "Ιατρικος",
  media: "Media",
  other: "Συνεργατης",
};

const SCOPE_META = {
  first_team: {
    badge: "Πρωτη Ομαδα",
    title: (
      <>
        ΣΥΝΕΡΓΑΤΕΣ <span className="text-[#F5A623]">ΠΡΩΤΗΣ ΟΜΑΔΑΣ</span>
      </>
    ),
    desc: "Οι στρατηγικοι συνεργατες που στηριζουν την αγωνιστικη δραστηριοτητα της Πρωτης Ομαδας του Λευτεριά FC.",
    seo: "Συνεργατες Πρωτης Ομαδας · Λευτεριά FC",
  },
  academy: {
    badge: "Ακαδημια",
    title: (
      <>
        ΣΥΝΕΡΓΑΤΕΣ <span className="text-[#F5A623]">ΑΚΑΔΗΜΙΑΣ</span>
      </>
    ),
    desc: "Οι στρατηγικοι συνεργατες που ενισχυουν το προγραμμα αναπτυξης της Ακαδημιας Λευτεριά FC.",
    seo: "Συνεργατες Ακαδημιας · Λευτεριά FC",
  },
  all: {
    badge: "Στρατηγικες Συνεργασιες",
    title: (
      <>
        ΛΕΥΤΕΡΙΑ FC <span className="text-[#F5A623]">PARTNERS</span>
      </>
    ),
    desc: "Ενισχυουμε το προγραμμα αναπτυξης μεσα απο συνεργασιες με ελιτ ακαδημιες, τεχνικους και επαγγελματιες παγκοσμιου επιπεδου.",
    seo: "Συνεργατες · Λευτεριά FC",
  },
};

const PartnersPage = () => {
  const [params] = useSearchParams();
  const rawScope = params.get("scope");
  const scope = rawScope === "first_team" || rawScope === "academy" ? rawScope : null;
  const meta = SCOPE_META[scope || "all"];

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = scope ? `${API}/partners?scope=${scope}` : `${API}/partners`;
    axios.get(url).then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [scope]);

  const contactCta = useMemo(() => {
    if (scope === "first_team") return "/contact?topic=partnership&team=first_team";
    if (scope === "academy") return "/contact?topic=partnership&team=academy";
    return "/contact?topic=partnership";
  }, [scope]);

  return (
    <div className="min-h-screen bg-black" data-testid="partners-page">
      <SEO title={meta.seo} description={meta.desc} />

      {/* Hero — left-aligned matching website style */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <span className="badge badge-secondary mb-4" data-testid="partners-scope-badge">{meta.badge}</span>
          <h1 className="font-['Bebas_Neue'] text-4xl md:text-5xl text-white tracking-wide">
            {meta.title}
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mt-6 leading-relaxed">{meta.desc}</p>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-6"><div className="h-px bg-[#262626]" /></div>

      {/* Partners grid */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-['Bebas_Neue'] text-3xl text-white mb-2">
            ΟΙ <span className="text-[#F5A623]">ΣΥΝΕΡΓΑΤΕΣ</span> ΜΑΣ
          </h2>
          <div className="w-12 h-1 bg-[#F5A623] mb-10" />

          {loading ? (
            <p className="text-zinc-500 text-sm text-center py-16">Φορτωση…</p>
          ) : items.length === 0 ? (
            <div className="bg-[#111] border border-dashed border-[#262626] rounded-2xl p-16 text-center">
              <Handshake size={40} className="text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">Δεν υπαρχουν ενεργοι συνεργατες αυτη τη στιγμη.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map(p => (
                <Link key={p.id} to={`/partners/${p.slug}`} data-testid={`partner-card-${p.slug}`}
                  className="group bg-[#111] border border-[#262626] rounded overflow-hidden hover:border-[#F5A623] transition-colors">
                  {p.hero_image_url ? (
                    <div className="h-44 bg-cover bg-center" style={{ backgroundImage: `url(${p.hero_image_url})` }} />
                  ) : (
                    <div className="h-44 bg-gradient-to-br from-[#1a1a1a] via-[#111] to-black flex items-center justify-center relative">
                      <ImageIcon size={32} className="text-zinc-700" />
                    </div>
                  )}
                  <div className="p-6">
                    <span className="badge badge-secondary mb-3 text-[10px]">{TYPE_LABELS[p.partner_type] || p.partner_type}</span>
                    <h3 className="font-['Bebas_Neue'] text-2xl text-white group-hover:text-[#F5A623] transition-colors tracking-wide">{p.name}</h3>
                    {p.tagline && <p className="text-sm text-zinc-400 mt-3 line-clamp-3 leading-relaxed">{p.tagline}</p>}
                    {p.executive_name && (
                      <p className="text-xs text-zinc-500 mt-4 pt-4 border-t border-[#262626]">
                        {p.executive_role || "Executive"}: <span className="text-zinc-300">{p.executive_name}</span>
                      </p>
                    )}
                    <span className="mt-4 inline-flex items-center gap-1 text-[#F5A623] text-sm font-semibold">
                      ΜΑΘΕ ΠΕΡΙΣΣΟΤΕΡΑ <ChevronRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Become a Partner CTA */}
          <div className="mt-16 bg-gradient-to-br from-[#F5A623]/10 via-[#111] to-black border border-[#F5A623]/20 rounded p-10 md:p-12">
            <h3 className="font-['Bebas_Neue'] text-2xl md:text-4xl text-white tracking-wide">
              ΓΙΝΕ <span className="text-[#F5A623]">ΣΥΝΕΡΓΑΤΗΣ</span>
            </h3>
            <p className="text-zinc-300 text-sm md:text-base mt-3 max-w-2xl leading-relaxed">
              Θελεις να ενωθεις με τη λιστα των συνεργατων μας? Ελα σε επαφη με την ομαδα μας για να συζητησουμε τις δυνατοτητες.
            </p>
            <Link to={contactCta}
              className="inline-flex items-center gap-2 mt-6 px-8 py-3 bg-[#F5A623] text-black font-semibold rounded hover:bg-[#e6951a] transition-colors text-sm"
              data-testid="become-partner-cta">
              ΕΠΙΚΟΙΝΩΝΙΑ <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;
